import React from 'react'
import { Table, Space, Tag, Button, theme } from 'antd'
import { EditOutlined, CheckOutlined } from '@ant-design/icons'
import { formatDateTime, formatPersonInfo, getStatusBadge, getDuration } from '../utils/formatters'
import { hexToRGBA } from '../../../../lib/utils'

// Function to get color based on activity type
const getActivityTypeColor = (activityType) => {
  switch (activityType) {
    case 'Medication Administration':
      return 'blue'
    case 'Vital Signs Check':
      return 'green'
    case 'Blood Glucose Monitoring':
      return 'purple'
    case 'Physical Therapy':
      return 'cyan'
    case 'IV Management':
      return 'geekblue'
    case 'Pain Assessment':
      return 'orange'
    case 'Ventilator Check':
      return 'volcano'
    case 'Wound Assessment':
      return 'magenta'
    case 'Medication Preparation':
      return 'gold'
    case 'Patient Education':
      return 'lime'
    case 'Chemotherapy Administration':
      return 'red'
    default:
      return 'default'
  }
}

const ActivityTable = ({ data }) => {
  const { token } = theme.useToken()

  const columns = [
    {
      title: 'Activity ID',
      dataIndex: 'activityId',
      key: 'activityId',
      width: 120
    },
    {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      key: 'caregiverName',
      render: (text, record) => formatPersonInfo(text, record.caregiverId),
      sorter: (a, b) => a.caregiverName.localeCompare(b.caregiverName)
    },
    {
      title: 'Patient',
      dataIndex: 'patientName',
      key: 'patientName',
      render: (text, record) => formatPersonInfo(text, record.patientId),
      sorter: (a, b) => a.patientName.localeCompare(b.patientName)
    },
    {
      title: 'Activity Type',
      dataIndex: 'activityType',
      key: 'activityType',
      render: (text) => <Tag color={getActivityTypeColor(text)}>{text}</Tag>,
      sorter: (a, b) => a.activityType.localeCompare(b.activityType)
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      sorter: (a, b) => a.department.localeCompare(b.department)
    },
    {
      title: 'Room',
      dataIndex: 'roomNumber',
      key: 'roomNumber'
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (date) => formatDateTime(date),
      sorter: (a, b) => new Date(a.startTime) - new Date(b.startTime)
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => getDuration(record.startTime, record.endTime)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusBadge(status)
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size='small'>
          <Button
            icon={<EditOutlined style={{ color: token.colorPrimary }} />}
            size='small'
            style={{
              background: hexToRGBA(token.colorPrimary, 0.1),
              borderColor: token.colorPrimary
            }}
          />
          {record.status !== 'Completed' && record.status !== 'Cancelled' && (
            <Button
              icon={<CheckOutlined style={{ color: token.colorSuccess }} />}
              size='small'
              style={{
                background: hexToRGBA(token.colorSuccess, 0.1),
                borderColor: token.colorSuccess,
                color: token.colorSuccess
              }}
            >
              Complete
            </Button>
          )}
        </Space>
      )
    }
  ]

  return <Table columns={columns} dataSource={data} rowKey='activityId' pagination={{ pageSize: 10 }} />
}

export default ActivityTable
